import { useMemo, useState } from 'react';
import { monthBounds } from '../domain/expenses';
import {
  BLANK_FILTER, expenseListRows, expenseFilterActive, nextExpenseSort, pageCount as pagesFor,
} from '../domain/expenseForms';
import { clampAmountDecimals } from '../domain/money';
import { useCurrency } from '../context/LocaleContext';

// The state behind the expenses list: which dates, which order, which filter,
// which page — and the rows that fall out of all four.
//
// Kept out of the page because it is the part with rules in it. The list's
// rules — the Sort menu's columns and their order, the Search menu's filter —
// are `src/domain/expenseForms.js`'s.
export { EXPENSE_SORT_COLUMNS as SORT_COLUMNS, BLANK_FILTER } from '../domain/expenseForms';

export default function useExpenseList({ betweenReport, initialMonth }) {
  // The list's own range, seeded from the current month but independent of it:
  // the summary answers "how did this month go", the list "show me these
  // transactions". The amount filter is typed, so it is parsed in the currency
  // the amounts were stored under (src/domain/locale.js).
  const currency = useCurrency();
  const [range, setRange] = useState(() => monthBounds(initialMonth));
  // Date descending is MMS's default; a null key means "no sort", which is the
  // state its Clear Sort lands on.
  const [sort, setSort] = useState({ key: 'occurredOn', desc: true });
  // The panel is edited freely and only applied on Search, as MMS does, so the
  // draft and the applied filter are two values.
  const [draft, setDraftState] = useState(BLANK_FILTER);

  // The By Amount box truncates a third decimal (#308): it is parsed by the
  // same `fromDecimal`, which rounds half-up, so a search for `50.999` would
  // show the $51.00 rows. Truncating makes the search be for the number on
  // screen.
  const setDraft = (next) => setDraftState({
    ...next,
    amount: clampAmountDecimals(next?.amount, currency),
  });
  const [filter, setFilter] = useState(BLANK_FILTER);
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);

  const { rows, truncated } = useMemo(() => {
    const report = betweenReport(range.start, range.end);
    // MMS's search is three separate filters, not one fuzzy match: the text box
    // is scoped to payee/payer, and category and amount are exact.
    return {
      truncated: report.truncated.length,
      rows: expenseListRows(report.instances, filter, sort, currency),
    };
  }, [betweenReport, range, filter, sort, currency]);

  const pageCount = pagesFor(rows.length, perPage);
  const shown = rows.slice((page - 1) * perPage, page * perPage);
  const isFiltered = expenseFilterActive(filter);

  // Anything that changes which rows exist sends you back to page one, or you
  // land on an empty page 4 of a 2-page result and it reads as "no matches".
  const setRangeAndReset = (next) => { setRange(next); setPage(1); };
  const applyFilter = (next) => { setFilter(next); setPage(1); };
  const clearFilter = () => { setDraftState(BLANK_FILTER); setFilter(BLANK_FILTER); setPage(1); };
  const setPerPageAndReset = (n) => { setPerPage(n); setPage(1); };

  // Choosing the active column flips its direction, which is how MMS's menu
  // reverses a sort.
  const sortBy = (key) => setSort((cur) => nextExpenseSort(cur, key));
  const clearSort = () => setSort({ key: null, desc: false });

  return {
    range, setRange: setRangeAndReset,
    sort, sortBy, clearSort,
    draft, setDraft, filter, applyFilter, clearFilter, isFiltered,
    perPage, setPerPage: setPerPageAndReset,
    page, setPage, pageCount,
    rows, shown, truncated,
  };
}
